import { Accordion, AccordionDetails, AccordionSummary, Grid } from '@mui/material';

import { useAppDispatch } from '../../hooks';
import { useAppContext } from '../../Context';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SyntheticEvent } from 'react';
import { setLoading } from '../../slices/miscSlice';
import { setLines } from '../../slices/linesSlice';

import ViewLines from './ViewLines';
import ViewCustomers from './ViewCustomers';
import ViewGroupsOT from './ViewGroupsOT';
import { setGroupsOT } from '../../slices/groupsOTSlice';
import { setGroups, setProcessRoles } from '../../slices/permissionsSlice';
import ViewRoles from './ViewRoles';
import ViewProcessRoles from './ViewProcessRoles';
import ViewProcessesSettings from './ViewProcessesSettings';
import ViewGroupOTProcesses from './ViewGroupOTProcesses';
import ViewSiigoIntegration from './ViewSiigoIntegration';
import ViewAlegraIntegration from './ViewAlegraIntegration';
import ViewProductionProcessRules from './ViewProductionProcessRules';
import { useGroups, usePermissions } from '../../services';
import { useAppSelector } from '../../hooks';
import { GroupOTType } from '../../types';
import { constants } from '../../constants';

const ViewSettings: React.FC = () => {
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const {
    canView_settings,
    canView_menu_settings,
    canView_group,
    canView_processrole,
    canView_line,
    canView_groupot,
    canView_process,
    canView_processgroupotline,
    canView_customer,
    canAdd_customer,
  } = usePermissions([
    'view_settings',
    'view_menu_settings',
    'view_group',
    'view_processrole',
    'view_line',
    'view_groupot',
    'view_process',
    'view_processgroupotline',
    'view_customer',
    'add_customer',
  ]);
  const { isAdministrador } = useGroups([constants.groups.GROUP_ADMINISTRADOR]);
  const { groupsOT } = useAppSelector((state) => state.groupsOT);
  const navigate = useNavigate();

  const [activePanel, setActivePanel] = useState<string | false>(false);
  const [activeGroupLineKey, setActiveGroupLineKey] = useState<string | null>(null);
  const [siigoConfigured, setSiigoConfigured] = useState(false);
  const [alegraConfigured, setAlegraConfigured] = useState(false);
  const [loadedSections, setLoadedSections] = useState({
    lines: false,
    groupsOT: false,
    roles: false,
    processRoles: false,
    groupLineProcesses: false,
  });

  if (!canView_settings && !canView_menu_settings) {
    navigate('/');
    return null;
  }

  const getLines = async () => {
    if (loadedSections.lines) return;
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'lines/', null, '', 'Error al obtener las líneas');
    dispatch(setLoading(false));
    dispatch(setLines(response));
    setLoadedSections((prev) => ({ ...prev, lines: true }));
  };

  const getGroupsOT = async () => {
    if (loadedSections.groupsOT) return;
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'groups-ot/', null, '', 'Error al obtener los grupos de orden de trabajo');
    dispatch(setLoading(false));
    dispatch(setGroupsOT(response));
    setLoadedSections((prev) => ({ ...prev, groupsOT: true, groupLineProcesses: true }));
  };

  const getGroupLineProcesses = async () => {
    if (loadedSections.groupLineProcesses) return;
    await getGroupsOT();
    setLoadedSections((prev) => ({ ...prev, groupLineProcesses: true }));
  };

  const getRoles = async () => {
    if (!canView_group || loadedSections.roles) return;
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'groups/', null, '', 'Error al obtener los roles');
    dispatch(setLoading(false));
    if (response) {
      dispatch(setGroups(response));
    }
    setLoadedSections((prev) => ({ ...prev, roles: true }));
  };

  const getProcessRoles = async () => {
    if (!canView_processrole || loadedSections.processRoles) return;
    dispatch(setLoading(true));
    const response = await apiCall('GET', 'process_roles/', null, '', 'Error al obtener los roles de procesos');
    dispatch(setLoading(false));
    if (response) {
      dispatch(setProcessRoles(response));
    }
    setLoadedSections((prev) => ({ ...prev, processRoles: true }));
  };

  const handlePanelChange =
    (panel: string, onExpand?: () => void) => (_event: SyntheticEvent, isExpanded: boolean) => {
      setActivePanel(isExpanded ? panel : false);
      if (isExpanded && onExpand) {
        onExpand();
      }
    };

  const handleLogoutAllSessions = async () => {
    const confirmed = window.confirm(
      '¿Estás seguro de cerrar todas las sesiones de tu empresa? Todos los usuarios deberán iniciar sesión nuevamente.',
    );
    if (!confirmed) return;
    dispatch(setLoading(true));
    const response = await apiCall('POST', 'security/logout_all/', null, '', 'Error al cerrar las sesiones');
    dispatch(setLoading(false));
    if (response) {
      localStorage.clear();
      navigate('/login');
    }
  };

  return (
    <div>
      <h1>Configuraciones</h1>
      <div className='container-wrap'>
        <Grid container spacing={5}>
          <Grid item xs={12} sm={6} className='text-left'>
            {canView_settings && (
              <>
                {canView_group && (
                  <Accordion onChange={handlePanelChange('roles', getRoles)}>
                  <AccordionSummary expandIcon={<span>▾</span>}>Roles</AccordionSummary>
                  <AccordionDetails>
                    <ViewRoles />
                  </AccordionDetails>
                </Accordion>
                )}
                {canView_process && (
                  <Accordion onChange={handlePanelChange('processes')}>
                  <AccordionSummary expandIcon={<span>▾</span>}>Procesos</AccordionSummary>
                  <AccordionDetails>
                    <ViewProcessesSettings active={activePanel === 'processes'} />
                  </AccordionDetails>
                </Accordion>
                )}
                {canView_process && (
                  <Accordion onChange={handlePanelChange('productionProcessRules')}>
                  <AccordionSummary expandIcon={<span>▾</span>}>
                    Reglas de Procesos de Producción
                  </AccordionSummary>
                  <AccordionDetails>
                    <ViewProductionProcessRules active={activePanel === 'productionProcessRules'} />
                  </AccordionDetails>
                </Accordion>
                )}
                {canView_processrole && (
                  <Accordion onChange={handlePanelChange('processRoles', getProcessRoles)}>
                  <AccordionSummary expandIcon={<span>▾</span>}>Roles de Procesos</AccordionSummary>
                  <AccordionDetails>
                    <ViewProcessRoles />
                  </AccordionDetails>
                </Accordion>
                )}
              </>
            )}
            {canView_line && (
              <Accordion onChange={handlePanelChange('lines', getLines)}>
                <AccordionSummary expandIcon={<span>▾</span>}>Lineas</AccordionSummary>
                <AccordionDetails>
                  <ViewLines hideActions={false} />
                </AccordionDetails>
              </Accordion>
            )}
            {canView_groupot && (
              <Accordion onChange={handlePanelChange('groupsOT', getGroupsOT)}>
                <AccordionSummary expandIcon={<span>▾</span>}>Grupos OT</AccordionSummary>
                <AccordionDetails>
                  <ViewGroupsOT />
                </AccordionDetails>
              </Accordion>
            )}
            {canView_settings && (
              <>
                {canAdd_customer && (
                  <Accordion>
                  <AccordionSummary expandIcon={<span>▾</span>}>Integración Siigo</AccordionSummary>
                  <AccordionDetails>
                    <ViewSiigoIntegration onConfiguredChange={setSiigoConfigured} />
                  </AccordionDetails>
                </Accordion>
                )}
                {canAdd_customer && (
                  <Accordion>
                  <AccordionSummary expandIcon={<span>▾</span>}>Integración Alegra</AccordionSummary>
                  <AccordionDetails>
                    <ViewAlegraIntegration onConfiguredChange={setAlegraConfigured} />
                  </AccordionDetails>
                </Accordion>
                )}
              </>
            )}
            {isAdministrador && (
              <Accordion>
                <AccordionSummary expandIcon={<span>▾</span>}>Seguridad</AccordionSummary>
                <AccordionDetails>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>Administración de sesiones de la empresa.</div>
                    <button type="button" className="btn-secondary" onClick={handleLogoutAllSessions}>
                      Cerrar todas las sesiones
                    </button>
                  </div>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
          <Grid item xs={12} sm={6} className='text-right'>

            {canView_processgroupotline && (
              <Accordion onChange={handlePanelChange('groupLineProcesses', getGroupLineProcesses)}>
                <AccordionSummary expandIcon={<span>▾</span>}>Procesos por Líneas</AccordionSummary>
                <AccordionDetails>
                  {groupsOT.flatMap((groupOT: GroupOTType) =>
                    groupOT.lines.map((line) => (
                      <Accordion
                        key={`${groupOT.id}-${line.id}`}
                        onChange={(_event, isExpanded) =>
                          setActiveGroupLineKey(isExpanded ? `${groupOT.id}-${line.id}` : null)
                        }
                      >
                        <AccordionSummary expandIcon={<span>▾</span>}>
                          Procesos para {groupOT.group_name} - {line.line_name}
                        </AccordionSummary>
                        <AccordionDetails>
                          <ViewGroupOTProcesses
                            groupOT={groupOT}
                            lineId={line.id}
                            showLineHeader={false}
                            active={activeGroupLineKey === `${groupOT.id}-${line.id}`}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )),
                  )}
                </AccordionDetails>
              </Accordion>
            )}
            {(siigoConfigured || alegraConfigured) && canView_customer && (
              <Accordion>
                <AccordionSummary expandIcon={<span>▾</span>}>Sincronizar Clientes</AccordionSummary>
                <AccordionDetails>
                  <ViewCustomers siigoConfigured={siigoConfigured} alegraConfigured={alegraConfigured} />
                </AccordionDetails>
              </Accordion>
            )}

          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default ViewSettings;
